import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Pricing Card Component
 * A composite component designed to display subscription plans, built upon base
 * components like Card and Button.
 *
 * @example
 * ```tsx
 * // Basic Usage
 * <PricingCard
 *   title="Plus Plan"
 *   price={10}
 *   currency="USD"
 *   period="month"
 *   features={['6,000 credits/month', 'Advanced AI Analysis', 'Priority Support']}
 *   onSelect={() => console.log('Selected Plus Plan')}
 * />
 *
 * <PricingCard
 *   title="Pro Plan"
 *   price={15}
 *   currency="USD"
 *   period="month"
 *   popular={true}
 *   popularLabel="Most Popular"
 *   features={['10,000 credits/month', 'API Access', 'Batch Translation']}
 *   onSelect={() => console.log('Selected Pro Plan')}
 * />
 *
 * // Current Plan Status
 * <PricingCard
 *   title="Free Plan"
 *   price={0}
 *   current={true}
 *   features={['500 trial credits', 'Basic Translation', 'PDF Word Selection']}
 *   disabled={true}
 * />
 *
 * // Loading State
 * <PricingCard
 *   title="Plus Plan"
 *   price={10}
 *   features={['6,000 credits/month']}
 *   loading={true}
 *   onSelect={() => {}}
 * />
 * ```
 */
import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { MemphisCard } from '../../core/Card/MemphisCard';
import { Button } from '../../core/Button';
/**
 * Formats the price for display.
 */
const formatPrice = (price, currency = 'USD') => {
    const currencySymbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        CNY: '¥',
    };
    const symbol = currencySymbols[currency] || currency;
    return `${symbol}${price}`;
};
/**
 * A dedicated card component for displaying subscription plans, integrating price,
 * features, and an action button.
 */
export const PricingCard = forwardRef(({ title, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
description, price, currency = 'USD', period = 'month', periodOverride, originalPrice, features, credits, isRecurringCredits = true, current = false, currentLabel = 'Current Plan', disabled = false, loading = false, buttonText, onSelect, secondaryButtonText, onSecondarySelect, secondaryButtonDisabled = false, secondaryButtonLoading = false, className, pattern, highlightColor, ...props }, ref) => {
    const getButtonText = () => {
        if (buttonText)
            return buttonText;
        if (current)
            return currentLabel;
        if (price === 0)
            return 'Get Started';
        return 'Choose Plan';
    };
    const getButtonVariant = () => {
        if (current)
            return 'secondary';
        return 'outline';
    };
    const isPrimaryButtonDisabled = disabled || current || loading;
    const isSecondaryButtonDisabled = secondaryButtonDisabled || current || secondaryButtonLoading;
    const highlightContent = (_jsx("div", { className: "text-center", children: _jsxs("div", { className: "flex items-baseline justify-center", children: [_jsx("span", { className: "text-2xl font-bold text-white", children: `${title}    ${formatPrice(price, currency)}` }), _jsx("span", { className: "text-white/70 ml-1", children: periodOverride ? `(${periodOverride})` : `/${period}` })] }) }));
    return (_jsxs(MemphisCard, { ...props, ref: ref, pattern: pattern, highlightColor: highlightColor, className: cn('relative', disabled && !current && 'opacity-60', className), highlightContent: highlightContent, children: [_jsx("div", { className: "mb-4 flex-grow", children: _jsxs("ul", { className: "space-y-1.5", children: [credits && (_jsxs("li", { className: "flex items-center text-gray-600", children: [_jsx("svg", { className: "w-4 h-4 text-interactive-emerald-flash mr-2 flex-shrink-0", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", clipRule: "evenodd" }) }), _jsxs("span", { className: "text-sm", children: [_jsx("strong", { children: credits.toLocaleString() }), " credits/", isRecurringCredits ? 'mo' : 'total'] })] })), features.map((feature, index) => (_jsxs("li", { className: "flex items-center text-gray-600", children: [_jsx("svg", { className: "w-4 h-4 text-interactive-emerald-flash mr-2 flex-shrink-0", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", clipRule: "evenodd" }) }), _jsx("div", { className: "text-sm min-w-0 break-words", children: typeof feature === 'string' ? _jsx("strong", { children: feature }) : feature })] }, index)))] }) }), _jsxs("div", { className: "flex w-full items-center gap-2", children: [_jsx(Button, { variant: getButtonVariant(), size: "small", disabled: isPrimaryButtonDisabled, loading: loading, onClick: onSelect, className: "w-full flex-1", children: getButtonText() }), secondaryButtonText && onSecondarySelect && (_jsx(Button, { variant: "primary", size: "small", disabled: isSecondaryButtonDisabled, loading: secondaryButtonLoading, onClick: onSecondarySelect, className: "w-full flex-1", children: secondaryButtonText }))] })] }));
});
PricingCard.displayName = 'PricingCard';
export default PricingCard;
//# sourceMappingURL=PricingCard.js.map